import { useState } from "react";
import {
  GetCoursesByStudentEmail,
  GetCoursesWithResultsByStudentId,
} from "../../actions/teacherQueries";
import StatusBadge from "../Badges/StatusBadge";
import { intervalToDuration } from "date-fns";
import { formatDate, toDate, compareDatesDesc } from "../../Utilities/dateUtils";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "reactstrap";
import FocusLossModal from "../Modal/FocusLossModal";
import StudentResultQuestionOverview from "../Results/StudentResultQuestionOverview";
import {
  getWinOptions,
  roundScore,
  isGy25TestModule,
} from "../../Utilities/moduleHelper";
import { LW_COURSE_API_URL } from "../../constants/api.constants";
import { useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLayerGroup,
  faBookOpen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { groupStudentFinalExamVariants } from "../../Utilities/groupFinalExamVariants";
import StudentResultTables from "./StudentResultTables";
import { PacmanLoader } from "react-spinners";
import FinalExamSourceIcon from "../Badges/FinalExamSourceIcon";
import useRemoveFinalExamVariantAccess from "../../hooks/useRemoveFinalExamVariantAccess";

const examAccentColor = (status, isGy25FinalExam) => {
  const map = isGy25FinalExam
    ? {
        Completed: "success",
        Done: "danger",
        InProgress: "warning",
        NotStarted: "secondary",
      }
    : {
        Completed: "success",
        Done: "success",
        InProgress: "warning",
        NotStarted: "secondary",
      };
  return map[status] ?? "secondary";
};

function StudentResultOverview({ studentEmail, studentId }) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const studentCourses = GetCoursesByStudentEmail(studentEmail);
  const coursesWithResults = GetCoursesWithResultsByStudentId(studentId);
  const [moduleId, setModuleId] = useState(null);
  const [courseId, setCourseId] = useState(null);
  const [moduleName, setModuleName] = useState(null);
  const [courseName, setCourseName] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const [latestResultIsOpen, setLatestResultIsOpen] = useState(true);
  const toggleLatest = () => setLatestResultIsOpen(!latestResultIsOpen);
  const [bestResultIsOpen, setBestResultIsOpen] = useState(true);
  const toggleBest = () => setBestResultIsOpen(!bestResultIsOpen);
  const [focusLossModal, setFocusLossModal] = useState({
    isOpen: false,
    runData: [],
    label: "",
    isFinalExam: false,
  });
  const closeFocusLossModal = () =>
    setFocusLossModal((prev) => ({ ...prev, isOpen: false }));

  const closeResult = () => {
    setModuleId(null);
    setCourseId(null);
    setModuleName(null);
    setCourseName(null);
    setVariantId(null);
  };

  const { setRemoveAccessPrompt, prompt: invalidateAccessPrompt } =
    useRemoveFinalExamVariantAccess(() => {
      queryClient.invalidateQueries({
        queryKey: ["FetchCoursesWithResultsByStudentId", studentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["FetchCoursesByStudentEmail", studentEmail],
      });
    });

  const viewAt2Result = (studentEmail, currentCourseId, currentModuleId) => {
    var winOptions = getWinOptions;
    window.open(
      `${LW_COURSE_API_URL}/Home/ViewModuleResult?` +
        "email=" +
        encodeURIComponent(studentEmail) +
        "&courseId=" +
        currentCourseId +
        "&moduleId=" +
        currentModuleId,
      "",
      winOptions
    );
  };
  const viewScormResult = (courseId, moduleId, courseName, moduleName) => {
    setModuleId(moduleId);
    setCourseId(courseId);
    setModuleName(moduleName);
    setCourseName(courseName);
    setVariantId(null);
    setBestResultIsOpen(true);
    setLatestResultIsOpen(true);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const viewGy25FinalExamResult = (
    courseId,
    moduleId,
    courseName,
    variantName,
    variantId
  ) => {
    setModuleId(moduleId);
    setCourseId(courseId);
    setModuleName(variantName);
    setCourseName(courseName);
    setVariantId(variantId);
    setBestResultIsOpen(true);
    setLatestResultIsOpen(true);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const columnExtensions = [
    { columnName: "name", width: 280 },
    { columnName: "showResult", width: 160 },
    { columnName: "status", width: 190 },
    { columnName: "donePercentage", width: 130 },
    { columnName: "score", width: 150 },
    { columnName: "nbrOfAttempts", width: 150 },
    { columnName: "focusLostCount", width: 130 },
    { columnName: "activityPoints", width: 160 },
    { columnName: "lastRun", width: 200 },
  ];

  const columns = [
    { title: <FormattedMessage id="Name" />, name: "name" },
    { title: <FormattedMessage id="ShowResult" />, name: "showResult" },
    { title: <FormattedMessage id="Status" />, name: "status" },
    { title: <FormattedMessage id="PercentDone" />, name: "donePercentage" },
    { title: <FormattedMessage id="ModuleTestScore" />, name: "score" },
    {
      title: <FormattedMessage id="NumberOfAttempts" />,
      name: "nbrOfAttempts",
    },
    {
      title: <FormattedMessage id="FocusControl" />,
      name: "focusLostCount",
    },
    { title: <FormattedMessage id="ActivityPoints" />, name: "activityPoints" },
    { title: <FormattedMessage id="LastRun" />, name: "lastRun" },
  ];

  if (studentCourses.isPending || coursesWithResults.isPending) {
    return (
      <div className="inline-pacman-loader">
        <PacmanLoader color="#20a8d8" size={20} />
      </div>
    );
  }

  const buildFocusLossCell = (focusLosses, label, isFinalExam = false) => {
    if (!focusLosses) return null;
    const total = focusLosses.length;
    if (total === 0) return null;

    const formatDuration = (ms, autoSubmit) => {
      if (ms == null) return autoSubmit ? "" : "-";
      const {
        hours = 0,
        minutes = 0,
        seconds = 0,
      } = intervalToDuration({ start: 0, end: Math.ceil(ms / 1000) * 1000 });

      if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
      if (minutes > 0) return `${minutes}m ${seconds}s`;

      return `${seconds}s`;
    };

    const runData = [...focusLosses]
      .sort(
        (a, b) =>
          a.runNumber - b.runNumber ||
          compareDatesDesc(a.lostFocus, b.lostFocus),
      )
      .map((loss) => ({
        runNumber: loss.runNumber,
        formattedTime: formatDate(loss.lostFocus, "d MMM yyyy HH:mm:ss"),
        formattedDuration: formatDuration(loss.durationMs, loss.autoSubmit),
        durationMs: loss.durationMs,
        focusControlType: loss.focusControlType,
        autoSubmit: loss.autoSubmit,
      }));

    return (
      <span
        className="focus-loss-modal__trigger"
        onClick={() =>
          setFocusLossModal({
            isOpen: true,
            runData,
            label,
            isFinalExam,
          })
        }
      >
        {total}
      </span>
    );
  };

  const getFocusLossesForModule = (courseId, moduleId) => {
    return (
      coursesWithResults.data
        ?.find((course) => course.id === courseId)
        ?.modules?.find((mod) => mod.moduleId === moduleId)
        ?.students?.find((stud) => stud.studentId === Number(studentId))
        ?.focusLosses ?? null
    );
  };

  let tableData = [];
  studentCourses.data.forEach((c) => {
    let lastRun = c.modules.some((mod) => mod.lastRun != null)
      ? new Date(Math.max(...c.modules.map((e) => toDate(e.lastRun))))
      : null;
    let course = {
      id: "c" + c.id,
      parentId: null,
      name: c.name,
      status: <StatusBadge status={c.status} />,
      donePercentage: c.progressPercentage,
      lastRun: lastRun === null ? null : formatDate(lastRun, "PPp"),
    };
    tableData.push(course);
    c.modules.forEach((m) => {
      let module = {};
      const isGy25FinalExam = m.isGy25 && m.isFinalExam;
      let gy25FinalExamStatus = null;
      if (isGy25FinalExam) {
        let currentModule = coursesWithResults.data
          ?.find((course) => course.id === c.id)
          ?.modules?.find((module) => module.moduleId === m.id);

        if (currentModule) {
          gy25FinalExamStatus = currentModule.students?.find(
            (stud) => stud.studentId === Number(studentId)
          );
        }
      }
      if (
        isGy25FinalExam &&
        gy25FinalExamStatus != null &&
        gy25FinalExamStatus.finalExamVariants.length > 1
      ) {
        module = {
          id: "c" + c.id + "m" + m.id,
          parentId: "c" + c.id,
          name: m.name,
          status: null,
          statusCode: null,
          score: null,
          nbrOfAttempts: null,
          activityPoints: null,
          lastRun: null,
          showResult: (
            <FormattedMessage
              values={{
                count: gy25FinalExamStatus.finalExamVariants.length,
              }}
              id="MultipleExamsNumberOfExams"
            />
          ),
          activityPointsPercentage: null,
          scorePercentage: null,
          isGy25FinalExam: null,
          isFinalExam: m.isFinalExam,
        };
      } else {
        if (isGy25TestModule(m) && m.moduleCoupleId) return;

        const isGy25FactModule = m.isGy25 && m.isFactModule && !m.isFinalExam;

        let coupledTestMod = null;

        if (isGy25FactModule && m.moduleCoupleId != null) {
          coupledTestMod = c.modules.find(
            (s) =>
              s.moduleCoupleId === m.moduleCoupleId &&
              s.id !== m.id &&
              isGy25TestModule(s)
          );
        }

        const hasFinalExamStatus =
          isGy25FinalExam && gy25FinalExamStatus != null;
        const hasCoupledTestMod = isGy25FactModule && coupledTestMod != null;

        const getStatusBadge = () => {
          return hasFinalExamStatus ? (
            <StatusBadge
              status={gy25FinalExamStatus.status}
              isGy25FinalExam={true}
            />
          ) : hasCoupledTestMod && coupledTestMod.status != null ? (
            <>
              <StatusBadge
                status={m.status}
                title={intl.formatMessage({ id: "StatusForFactModule" })}
              />
              <StatusBadge
                status={coupledTestMod.status}
                title={intl.formatMessage({ id: "StatusForTestModule" })}
                isTestModule={true}
              />
            </>
          ) : (
            <StatusBadge status={m.status} />
          );
        };

        const moduleScore = hasFinalExamStatus
          ? gy25FinalExamStatus.score
          : hasCoupledTestMod && coupledTestMod.score != null
            ? coupledTestMod.score
            : m.score;

        const moduleMaxScore = hasFinalExamStatus
          ? gy25FinalExamStatus.maxScore
          : hasCoupledTestMod && coupledTestMod.maxScore != null
            ? coupledTestMod.maxScore
            : m.maxScore;

        const getModuleScore = () => {
          if (moduleScore == null) return 0;

          const roundedScore = roundScore(moduleScore);

          if (!moduleMaxScore || moduleMaxScore <= 0) {
            return roundedScore;
          }

          const percentage = hasFinalExamStatus
            ? gy25FinalExamStatus.scoreInPercent
            : hasCoupledTestMod && coupledTestMod.score != null
              ? coupledTestMod.scoreInPercent
              : m.scoreInPercent;

          return `${roundedScore}/${moduleMaxScore} (${percentage}%)`;
        };

        const getNbrOfAttempt = () => {
          return hasCoupledTestMod && coupledTestMod.nbrOfAttempts > 0
            ? coupledTestMod.nbrOfAttempts
            : m.nbrOfAttempts > 0
              ? m.nbrOfAttempts
              : null;
        };

        const getActivityPoints = () => {
          if (isGy25FinalExam) return null;

          if (m.activityPoints != null && m.maxActivityPoints != null) {
            const percentage =
              m.maxActivityPoints > 0
                ? Math.round((m.activityPoints / m.maxActivityPoints) * 100)
                : 0;
            return `${m.activityPoints}/${m.maxActivityPoints} (${percentage}%)`;
          }

          return 0;
        };

        const getLastRun = () => {
          if (hasFinalExamStatus && gy25FinalExamStatus.lastRun != null) {
            return formatDate(gy25FinalExamStatus.lastRun, "PPp");
          }

          if (hasCoupledTestMod && coupledTestMod.lastRun != null) {
            return formatDate(coupledTestMod.lastRun, "PPp");
          }

          if (m.lastRun != null) {
            return formatDate(m.lastRun, "PPp");
          }

          return null;
        };

        const renderButton = () => (
          <Button
            className="btn-sm"
            color="primary"
            outline
            onClick={() => {
              if (m.moduleType === 1) {
                viewAt2Result(studentEmail, c.id, m.id);
              } else {
                viewScormResult(
                  c.id,
                  coupledTestMod?.id || m.id,
                  c.name,
                  m.name
                );
              }
              queryClient.invalidateQueries({ queryKey: [
                "FetchCoursesByStudentEmail",
                studentEmail,
              ] });
              queryClient.invalidateQueries({ queryKey: [
                "FetchCoursesWithResultsByStudentId",
                studentId,
              ] });
            }}
          >
            <FormattedMessage id="ShowResult" />
          </Button>
        );

        const showResultButton = () => {
          if (isGy25FactModule && hasCoupledTestMod) {
            const testHasRun = coupledTestMod.lastRun != null;
            if (!testHasRun) return <></>;

            return renderButton();
          }

          if (m.lastRun === null || (isGy25FactModule && !hasCoupledTestMod))
            return <></>;

          return renderButton();
        };

        const getScorePercentage = () => {
          if (
            hasCoupledTestMod &&
            coupledTestMod.score != null &&
            coupledTestMod.maxScore > 0
          ) {
            return Math.round(
              (coupledTestMod.score / coupledTestMod.maxScore) * 100
            );
          }

          if (m.score != null && m.maxScore > 0) {
            return Math.round((m.score / m.maxScore) * 100);
          }

          return 0;
        };

        module = {
          id: "c" + c.id + "m" + m.id,
          parentId: "c" + c.id,
          name: m.name,
          status: getStatusBadge(),
          statusCode:
            hasCoupledTestMod && coupledTestMod.status != null
              ? coupledTestMod.status
              : m.status,
          score: getModuleScore(),
          nbrOfAttempts: getNbrOfAttempt(),
          focusLostCount: m.maxScoreIsSetByModule
            ? buildFocusLossCell(
                getFocusLossesForModule(c.id, coupledTestMod?.id || m.id),
                m.name,
              )
            : null,
          activityPoints: getActivityPoints(),
          lastRun: getLastRun(),
          showResult: showResultButton(),
          activityPointsPercentage:
            m.activityPoints != null && m.maxActivityPoints > 0
              ? Math.round((m.activityPoints / m.maxActivityPoints) * 100)
              : 0,
          scorePercentage: getScorePercentage(),
          isGy25FinalExam: isGy25FinalExam,
          isFinalExam: m.isFinalExam,
          accentColor: examAccentColor(
            hasFinalExamStatus ? gy25FinalExamStatus.status : m.status,
            hasFinalExamStatus
          ),
        };
      }
      tableData.push(module);
      if (isGy25FinalExam && gy25FinalExamStatus != null) {
        const examMap = groupStudentFinalExamVariants(
          gy25FinalExamStatus.finalExamVariants
        );

        if (examMap.length > 0) {
          for (const { parent, children } of examMap) {
            children.sort(
              (a, b) => compareDatesDesc(a.lastRun, b.lastRun)
            );

            let parentVariant = {
              id: "parent" + parent.variantId,
              parentId: "c" + c.id + "m" + m.id,
              isDerivedParent: parent.isDerivedParent,
              accentColor: examAccentColor(parent.status, true),
              name: (
                <>
                  <FinalExamSourceIcon
                    isFromAnotherSchool={parent.isFromAnotherSchool}
                    isFromAnotherClass={parent.isFromAnotherClass}
                  />
                  {
                    <span title={intl.formatMessage({ id: "MainExam" })}>
                      <FontAwesomeIcon
                        icon={faBookOpen}
                        className="exam-icon me-2 text-primary"
                      />
                    </span>
                  }
                  {parent.name}
                </>
              ),
              status: !parent.isDerivedParent && (
                <StatusBadge status={parent.status} isGy25FinalExam={true} />
              ),
              statusCode: 0,
              score:
                parent.score != null
                  ? `${roundScore(parent.score)}/${parent.maxScore ?? ""} (${
                      parent.scoreInPercent
                    }%)`
                  : "",
              nbrOfAttempts: parent.lastRun == null ? "-" : 1,
              activityPoints: null,
              lastRun:
                parent.lastRun != null
                  ? formatDate(parent.lastRun, "PPp")
                  : null,
              showResult: !parent.isDerivedParent ? (
                <div className="d-flex flex-nowrap align-items-center justify-content-start gap-2">
                  {parent.status !== "NotStarted" ? (
                    <Button
                      className="btn-sm"
                      color="primary"
                      outline
                      onClick={() => {
                        viewGy25FinalExamResult(
                          c.id,
                          m.id,
                          c.name,
                          parent.name,
                          parent.variantId
                        );
                        queryClient.invalidateQueries({ queryKey: [
                          "FetchCoursesByStudentEmail",
                          studentEmail,
                        ] });
                        queryClient.invalidateQueries({ queryKey: [
                          "FetchCoursesWithResultsByStudentId",
                          studentId,
                        ] });
                      }}
                    >
                      <FormattedMessage id="ShowResult" />
                    </Button>
                  ) : parent.isFromAnotherSchool ? (
                    <Button
                      className="btn-sm invisible"
                      color="primary"
                      outline
                    >
                      <FormattedMessage id="ShowResult" />
                    </Button>
                  ) : null}
                  {parent.isFromAnotherSchool && (
                    <Button
                      className="btn-sm"
                      color="danger"
                      outline
                      onClick={() => setRemoveAccessPrompt(parent.accessId)}
                      title={intl.formatMessage({ id: "Delete" })}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  )}
                </div>
              ) : null,
              activityPointsPercentage: "-",
              scorePercentage: parent.scoreInPercent,
              focusLostCount: !parent.isDerivedParent
                ? buildFocusLossCell(
                    gy25FinalExamStatus.focusLosses?.filter(
                      (l) =>
                        l.studentId === Number(studentId) &&
                        l.finalExamVariantAccessId === parent.accessId,
                    ) ?? null,
                    parent.name,
                    true,
                  )
                : null,
            };

            tableData.push(parentVariant);

            children.forEach((child) => {
              let childtVariant = {
                id: "child" + child.variantId,
                parentId: "parent" + parent.variantId,
                accentColor: examAccentColor(child.status, true),
                name: (
                  <span className="ms-4">
                    <FinalExamSourceIcon
                      isFromAnotherSchool={child.isFromAnotherSchool}
                      isFromAnotherClass={child.isFromAnotherClass}
                    />
                    {
                      <span title={intl.formatMessage({ id: "Retest" })}>
                        <FontAwesomeIcon
                          icon={faLayerGroup}
                          className="exam-icon me-2 text-primary"
                        />
                      </span>
                    }
                    {child.name}
                  </span>
                ),
                status: (
                  <StatusBadge status={child.status} isGy25FinalExam={true} />
                ),
                statusCode: 0,
                score:
                  child.score != null
                    ? `${roundScore(child.score)}/${child.maxScore ?? ""} (${
                        child.scoreInPercent
                      }%)`
                    : "",
                nbrOfAttempts: child.lastRun == null ? "-" : 1,
                activityPoints: null,
                lastRun:
                  child.lastRun != null
                    ? formatDate(child.lastRun, "PPp")
                    : null,
                showResult: (
                  <div className="d-flex flex-nowrap align-items-center justify-content-start gap-2">
                    {child.status !== "NotStarted" ? (
                      <Button
                        className="btn-sm"
                        color="primary"
                        outline
                        onClick={() => {
                          viewGy25FinalExamResult(
                            c.id,
                            m.id,
                            c.name,
                            child.name,
                            child.variantId
                          );
                          queryClient.invalidateQueries({ queryKey: [
                            "FetchCoursesByStudentEmail",
                            studentEmail,
                          ] });
                          queryClient.invalidateQueries({ queryKey: [
                            "FetchCoursesWithResultsByStudentId",
                            studentId,
                          ] });
                        }}
                      >
                        <FormattedMessage id="ShowResult" />
                      </Button>
                    ) : child.isFromAnotherSchool ? (
                      <Button
                        className="btn-sm invisible"
                        color="primary"
                        outline
                      >
                        <FormattedMessage id="ShowResult" />
                      </Button>
                    ) : null}
                    {child.isFromAnotherSchool && (
                      <Button
                        className="btn-sm"
                        color="danger"
                        outline
                        onClick={() => setRemoveAccessPrompt(child.accessId)}
                        title={intl.formatMessage({ id: "Delete" })}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    )}
                  </div>
                ),
                activityPointsPercentage: "-",
                scorePercentage: child.scoreInPercent,
                focusLostCount: buildFocusLossCell(
                  gy25FinalExamStatus.focusLosses?.filter(
                    (l) =>
                      l.studentId === Number(studentId) &&
                      l.finalExamVariantAccessId === child.accessId,
                  ) ?? null,
                  child.name,
                  true,
                ),
              };

              tableData.push(childtVariant);
            });
          }
        }
      }
    });
  });

  return (
    <div>
      <StudentResultQuestionOverview
        studentId={studentId}
        courseId={courseId}
        moduleId={moduleId}
        moduleName={moduleName}
        courseName={courseName}
        bestResultIsOpen={bestResultIsOpen}
        toggleBest={toggleBest}
        latestResultIsOpen={latestResultIsOpen}
        toggleLatest={toggleLatest}
        isGy25FinalExam={variantId != null}
        variantId={variantId}
        onClose={closeResult}
      />
      <StudentResultTables
        tableData={tableData}
        columns={columns}
        columnExtensions={columnExtensions}
      />
      <FocusLossModal
        isOpen={focusLossModal.isOpen}
        onClose={closeFocusLossModal}
        runData={focusLossModal.runData}
        label={focusLossModal.label}
        isFinalExam={focusLossModal.isFinalExam}
      />
      {invalidateAccessPrompt}
    </div>
  );
}

export default StudentResultOverview;
